import './ViewSelector.scss';

import { createAsyncSelectors } from '@lib/selectors';
import { PaginationDirection } from '@sonar-software/sonar-ts-types';
import { ViewLabel } from './ViewLabel';

import { COPY_SPECIFIC, DEFAULT_FILTER } from '@sonar-software/sonar-ts-constants';
import { coreActions, DataDictionaryView } from '@sonar-software/sonar-core';
import { CreateView, ViewQueryParams } from './CreateView';
import React, { useEffect, useMemo } from 'react';
import Select, { DefaultOptionType } from 'antd/es/select/index';
import { UpdateQueryParams, useAction, useAsyncState } from '@hooks';

function buildOptions(
	views: Array<DataDictionaryView> | null | undefined,
	queryParams: ViewQueryParams
): Array<DefaultOptionType> {
	const viewOptions: Array<DefaultOptionType> =
		views?.map((view) => ({
			label: <ViewLabel
				key={ view.id }
				queryParams={ queryParams }
				view={ view } />,
			title: view.name,
			value: view.id
		})) ?? [];

	viewOptions.unshift({
		label: COPY_SPECIFIC.DEFAULT_VIEW,
		value: 0
	});

	return viewOptions;
}

export interface ViewSelectorProps {
	queryParams: ViewQueryParams;
	updateQuery: UpdateQueryParams<ViewQueryParams>;
}

const viewsSelectors = createAsyncSelectors((state) => state.core.dataDictionary.views.get);

export const ViewSelector: React.FC<ViewSelectorProps> = ({ queryParams, updateQuery }) => {
	const { data, isRequested } = useAsyncState(viewsSelectors);

	const loadViews = useAction(coreActions.requestGetCoreDataDictionaryViews);

	useEffect(() => {
		if (
			queryParams.sonarObjectId ||
			queryParams.sonarObjectType ||
			queryParams.tagId ||
			queryParams.applicationId ||
			Number.isInteger(queryParams.complianceCategory)
		) {
			loadViews({
				applicationId: queryParams.applicationId,
				complianceCategory: queryParams.complianceCategory,
				sonarObjectId: queryParams.sonarObjectId,
				sonarObjectType: queryParams.sonarObjectType,
				tagId: queryParams.tagId
			});
		}
	}, [
		loadViews,
		queryParams.applicationId,
		queryParams.sonarObjectId,
		queryParams.sonarObjectType,
		queryParams.tagId,
		queryParams.complianceCategory
	]);

	useEffect(() => {
		if (data && queryParams.lastViewId && !data.results.find((entry) => entry.id === queryParams.lastViewId)) {
			updateQuery({
				lastViewId: undefined,
				viewName: undefined,
				viewSkip: true
			});
		}
	}, [ data, queryParams.lastViewId, updateQuery ]);

	const options = useMemo(() => buildOptions(data?.results, queryParams), [ data?.results, queryParams ]);

	if (!queryParams || (
		!queryParams.sonarObjectId &&
		!queryParams.sonarObjectType &&
		!queryParams.applicationId &&
		!queryParams.tagId &&
		!Number.isInteger(queryParams.complianceCategory)
	)) {
		return null;
	}

	const createView =
		<CreateView
			onFinish={ (viewId, viewName) => {
				updateQuery({
					lastViewId: viewId,
					viewName
				});
			} }
			queryParams={ queryParams } />;

	return (
		<Select
			className='view-selector'
			classNames={ {
				popup: {
					root: 'view-selector-dropdown'
				}
			} }
			labelRender={ (props) => (isRequested ? queryParams.viewName ?? '' : props.label) }
			loading={ isRequested }
			onSelect={ (value, option) => {
				updateQuery({
					columns: '',
					filters: value ? '' : DEFAULT_FILTER,
					key: '',
					lastViewId: undefined,
					ordering: '',
					paginationDirection: PaginationDirection.Forward,
					viewId: value || undefined,
					viewName: option.title
				});
			} }
			options={ options }
			popupMatchSelectWidth={ false }
			popupRender={ (menu) =>
				<>
					{ menu }
					{ createView }
				</> }
			size='large'
			value={ queryParams.viewId ?? queryParams.lastViewId ?? 0 } />
	);
};
